/**
 * Caps and field schemas of the spreadsheet op vocabulary (`ops.ts`).
 *
 * Messages never echo what the model sent: enums list the allowed values,
 * strict objects list the allowed fields, and cell messages name the field.
 * (A Zod issue object still carries the offending key or value in `keys` /
 * `received`; log `message` and `path`, never the raw issue.)
 */
import { z } from 'zod';
import { hasControlChar } from './ranges';
export const MAX_OPS_PER_BATCH = 50;
export const MAX_TOUCHED_CELLS_PER_BATCH = 20_000;
/** Excel's limit on the text in one cell. */
export const MAX_CELL_TEXT_CHARS = 32_767;
/** Excel's limit on the length of one formula. */
export const MAX_FORMULA_CHARS = 8_192;
export const MAX_NUMBER_FORMAT_CHARS = 64;
export const MAX_CHART_TITLE_CHARS = 200;
export const CHART_TYPES = ['column', 'bar', 'line'];
export const H_ALIGNS = ['left', 'center', 'right'];
/** Patterns the prompt offers; any other bounded pattern is also accepted. */
export const COMMON_NUMBER_FORMATS = [
    '0',
    '0.00',
    '#,##0',
    '#,##0.00',
    '0%',
    '0.0%',
    '0.00%',
    '0.00E+00',
    'yyyy-mm-dd',
    'yyyy-mm-dd hh:mm',
    'hh:mm:ss',
    '@',
];
/** An object that refuses unknown keys without naming them. */
export const strictObject = (shape) => z.strictObject(shape, {
    error: (issue) => issue.code === 'unrecognized_keys'
        ? `unknown field; allowed fields are ${Object.keys(shape).join(', ')}`
        : undefined,
});
/** An enum whose every error lists the allowed values, never the one sent. */
export const oneOf = (field, values) => z.enum(values, { error: () => `${field} must be one of ${values.join(', ')}` });
/**
 * A finite number. Zod 4's number type already refuses NaN and Infinity, as
 * an `invalid_type` issue whose default message reads like a type mismatch;
 * this keeps the message Zod 3's `.finite()` gave.
 */
export const finiteNumberSchema = z.number({
    error: (issue) => (typeof issue.input === 'number' ? 'must be a finite number' : undefined),
});
export const colorSchema = z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/, 'must be a hex color like #1A73E8');
/**
 * One cell value: the scalar `write_spreadsheet_row` accepts. Text whose
 * trimmed start is `=` is refused: Univer would store it as a formula, and a
 * value must never silently become one. Literal text starting with `=` is out
 * of scope for v1.
 */
export const cellValueSchema = z.union([
    z
        .string()
        .max(MAX_CELL_TEXT_CHARS, `may be at most ${MAX_CELL_TEXT_CHARS} characters`)
        .refine((v) => !v.trim().startsWith('='), 'starts with =; put formulas in a setFormulas op'),
    finiteNumberSchema,
    z.boolean(),
    z.null(),
]);
export const formulaSchema = z
    .string({
    // Zod 3's invalid_type_error: a missing value keeps the default message.
    error: (issue) => issue.input === undefined ? undefined : 'must be a formula string starting with =',
})
    .max(MAX_FORMULA_CHARS, `may be at most ${MAX_FORMULA_CHARS} characters`)
    .refine((f) => f.startsWith('='), 'must start with =')
    .refine((f) => !f.startsWith('=') || f.slice(1).trim().length > 0, 'needs an expression after =');
export const numberFormatSchema = z
    .string()
    .min(1, 'pattern must not be empty')
    .max(MAX_NUMBER_FORMAT_CHARS, `pattern may be at most ${MAX_NUMBER_FORMAT_CHARS} characters`)
    .refine((p) => !hasControlChar(p), 'pattern must not contain control characters');
//# sourceMappingURL=op-fields.js.map